import React from "react";
import { useController } from "react-hook-form";
import { useMask } from "@react-input/mask";
import { classes } from "../../util/style-utils";
import { COUNTRYCODE_OPTIONS } from "@geicoap/ia-shared";

/**
 * A controlled phone number input component with automatic formatting and validation.
 * Uses react-hook-form for form state management and applies a US phone number mask.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.control - React Hook Form control object for form state management
 * @param {string} [props.name="phoneNumber"] - The name/identifier for the form field
 * @param {string} [props.label] - Optional label text to display above the input
 * @param {string} [props.placeholder="(___) ___-____"] - Placeholder text showing the expected format
 * @param {boolean} [props.disabled=false] - Whether the input should be disabled
 * @param {string} [props.descriptiveText] - Optional descriptive text to display below the label
 * 
 * @returns {JSX.Element} A formatted phone number input field with label, validation, and error handling
 * 
 * @example
 * // Basic usage with react-hook-form
 * <PhoneNumber 
 *   control={control} 
 *   name="contactPhone" 
 *   label="Phone Number" 
 * />
 * 
 * @example
 * // With additional props
 * <PhoneNumber 
 *   control={control}
 *   name="emergencyContact"
 *   label="Emergency Contact"
 *   descriptiveText="Please provide a 10-digit phone number"
 *   disabled={false}
 * />
 */
export default function PhoneNumber({
    control,
    name = "phoneNumber",
    label,
    placeholder = "(___) ___-____",
    disabled = false,
    descriptiveText,
    showCountryCode = false,
    countryCodeName,
    countryCodeLabel,
    countryCodePlaceholder,
    countryCodeOptions = COUNTRYCODE_OPTIONS,
    countryCodeDefaultValue = "+1"
}) {
    const resolvedCountryCodeName = countryCodeName || `${name}CountryCode`;

    /**
     * Hook that connects the component to react-hook-form
     * @type {Object}
     * @property {Object} field - Field methods and state
     * @property {Function} field.onChange - Handler for value changes
     * @property {Function} field.onBlur - Handler for blur events
     * @property {string} field.value - Current field value
     * @property {Function} field.ref - Reference callback for the input element
     * @property {Object} fieldState - Field validation state
     * @property {Object|undefined} fieldState.error - Validation error object if present
     */
    const {
        field: { onChange, onBlur, value, ref },
        fieldState: { error }
    } = useController({
        name,
        control,
        defaultValue: ""
    });

    const {
        field: {
            onChange: onCountryCodeChange,
            onBlur: onCountryCodeBlur,
            value: countryCodeValue,
            ref: countryCodeRef
        },
        fieldState: { error: countryCodeError }
    } = useController({
        name: resolvedCountryCodeName,
        control,
        defaultValue: countryCodeDefaultValue
    });

    const shouldUseUsMask = !showCountryCode || countryCodeValue === "+1";

    /**
     * Input mask hook that applies phone number formatting (___) ___-____
     * @type {Object} Reference to the masked input element
     */
    const maskRef = useMask({
        mask: '(___) ___-____',
        replacement: { _: /\d/ }
    });

    /**
     * Combines react-hook-form ref with input mask ref to ensure both work together
     * @param {HTMLInputElement} element - The input element to attach refs to
     */
    const handleMaskedRef = (element) => {
        ref(element);
        maskRef.current = element;
    };

    const handleUnmaskedRef = (element) => {
        ref(element);
        maskRef.current = null;
    };

    const handleInternationalPhoneChange = (event) => {
        const sanitizedValue = event.target.value.replace(/\D/g, "").slice(0, 15);
        onChange(sanitizedValue);
    };

    const handleCountryCodeChange = (event) => {
        onCountryCodeChange(event);
        onChange("");
    };

    return (
        <>
            {((showCountryCode && countryCodeLabel) || label || descriptiveText) && (
                <div className="flex-row label-row">
                    {showCountryCode && countryCodeLabel && (
                        <div className={classes("form-field", countryCodeError && "form-field--error")}>
                            <label htmlFor={`${resolvedCountryCodeName}-input`} className="text">
                                {countryCodeLabel}
                            </label>
                        </div>
                    )}

                    {(label || descriptiveText) && (
                        <div className={classes("form-field", error && "form-field--error")}>
                            {label && (
                                <label htmlFor={`${name}-input`} className="text">
                                    {label}
                                </label>
                            )}

                            {descriptiveText && (
                                <span className="form-descriptive-copy">
                                    {descriptiveText}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="flex-row">
                {showCountryCode && (
                    <div className={classes("form-field", countryCodeError && "form-field--error")}>
                        <div className="select-box">
                            <select
                                id={`${resolvedCountryCodeName}-input`}
                                ref={countryCodeRef}
                                value={countryCodeValue || ""}
                                onChange={handleCountryCodeChange}
                                onBlur={onCountryCodeBlur}
                                disabled={disabled}
                            >
                                {countryCodePlaceholder && <option value="">{countryCodePlaceholder}</option>}
                                {countryCodeOptions.map(option => (
                                    <option value={option.value} key={option.value}>{`${option.label}`}</option>
                                ))}
                            </select>
                        </div>
                        {countryCodeError && (
                            <span className="form-message">
                                {countryCodeError.message}
                            </span>
                        )}
                    </div>
                )}

                <div className={classes("form-field", error && "form-field--error")}>
                    {shouldUseUsMask ? (
                        <input
                            key={`${name}-us-mask`}
                            id={`${name}-input`}
                            type="text"
                            className="phone-number"
                            placeholder={placeholder}
                            ref={handleMaskedRef}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            disabled={disabled}
                            autoComplete="tel"
                        />
                    ) : (
                        <input
                            key={`${name}-intl-no-mask`}
                            id={`${name}-input`}
                            type="text"
                            className="phone-number"
                            ref={handleUnmaskedRef}
                            value={value}
                            onChange={handleInternationalPhoneChange}
                            onBlur={onBlur}
                            inputMode="numeric"
                            maxLength={15}
                            disabled={disabled}
                            autoComplete="tel"
                        />
                    )}
                    {error && (
                        <span className="form-message">
                            {error.message}
                        </span>
                    )}
                </div>
            </div>
        </>
    );
}
